import React, { useEffect, useState } from 'react';
import Card from '../common/Card';
import Button from '../common/Button';
import Loading from '../common/Loading';
import { systemAPI } from '../../services/api';
import { handleApiError } from '../../utils/helpers';

const SystemSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [trialEnabled, setTrialEnabled] = useState(true);
  const [trialDays, setTrialDays] = useState(14);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await systemAPI.getSettings();
        const settings = response.data.settings || {};
        setTrialEnabled(settings.trial_enabled ?? true);
        setTrialDays(settings.trial_days ?? 14);
      } catch (err) {
        setError(handleApiError(err));
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      await systemAPI.updateSettings({
        trial_enabled: trialEnabled,
        trial_days: Number(trialDays),
      });
      setSuccess('System settings updated successfully');
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-sky-600">
            Super Admin
          </p>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-1">
            System Settings
          </h1>
          <p className="text-slate-600 text-sm sm:text-base mt-1">
            Configure global options such as free trial availability and duration.
          </p>
        </div>
      </Card>

      <Card className="p-6 border border-slate-200 rounded-2xl bg-white">
        <form onSubmit={handleSave} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              {error}
            </div>
          )}
          {success && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
              {success}
            </div>
          )}

          {/* Free Trial Toggle */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Free Trial for New Agencies
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                When enabled, new agencies that sign up from the landing page will receive a free trial period before payment is required.
              </p>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <span className="mr-3 text-sm text-slate-700">
                {trialEnabled ? 'Enabled' : 'Disabled'}
              </span>
              <div className="relative">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={trialEnabled}
                  onChange={(e) => setTrialEnabled(e.target.checked)}
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:bg-emerald-500 transition-colors" />
                <div
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    trialEnabled ? 'translate-x-5' : ''
                  }`}
                />
              </div>
            </label>
          </div>

          {/* Trial Days */}
          <div className="border-t border-slate-200 pt-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Free Trial Duration (days)
            </label>
            <div className="max-w-xs">
              <input
                type="number"
                min="0"
                max="365"
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
                className="input w-full"
              />
              <p className="text-xs text-slate-500 mt-1">
                Set to 0 if you want accounts to be created without any trial period even when the toggle is enabled.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end pt-4 border-t border-slate-100">
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default SystemSettingsPage;

